import templateUrl from './dateTimePicker.html';

export default angular.module('eventix.common.directives.datePickers.dateTimePicker', [])
    .component('datetimePicker', {
        /**
         * @ngdoc directive
         * @name eventix.common.directives:datetimePicker
         * @restrict element
         * @description
         * Proxy for `eonasdan-bootstrap-datetimepicker`
         *
         * @example
         * <pre>
         * <datetime-picker ng-model="vm.datetime" timezone="'Europe/Paris'"></datetime-picker>
         * </pre>
         *
         * @param {String} ngModel Model attributed that can hold initial value and will receive picked values
         * @param {Boolean} disabled Whether to disable the picker
         * @param {Object} timezone Optional timezone object that will be used to format the model. You can use timezone
         * as a boolean attribute to simple enable the timezone picker interface
         */
        require: {
            ngModel: 'ngModel'
        },
        bindings: {
            timezone: '=?'
        },
        templateUrl,
        controller: function($scope, $element, $attrs, Locales, $window) {
            const $ctrl = this;
            const f = {
                output: 'YYYY-MM-DDTHH:mm:ssZ',
                input: 'YYYY-MM-DDTHH:mm:ss',
                view: 'DD-MM-YYYY HH:mm'
            };
            const utcOffsets = [
                '-12:00', '-11:00', '-10:00', '-09:30', '-09:00', '-08:00', '-07:00', '-06:00', '-05:00', '-04:00',
                '-03:30', '-03:00', '-02:00', '-01:00', '+00:00', '+01:00', '+02:00', '+03:00', '+03:30', '+04:00',
                '+04:30', '+05:00', '+05:30', '+05:45', '+06:00', '+06:30', '+07:00', '+08:00', '+08:30', '+08:45',
                '+09:00', '+09:30', '+10:00', '+10:30', '+11:00', '+12:00', '+12:45', '+13:00', '+14:00'
            ];
            const timezoneFilter = /^GMT|^Etc|^[A-Z0-9-]+$/;

            $ctrl.clearInput = clearInput;

            $ctrl.$onInit = function() {
                $ctrl.timezones = moment.tz.names().filter(z => !timezoneFilter.test(z)).concat(utcOffsets);
                $ctrl.inputName = ($element.attr('ng-model') || '').toString().split('.').slice(-1).join('');
                $ctrl.input = $element.find('input');
                $ctrl.input.datetimepicker({
                    allowInputToggle: true,
                    sideBySide: $window.innerWidth > 768,
                    useCurrent: false,
                    icons: {
                        time: 'fa fa-clock-o',
                        date: 'fa fa-calendar',
                        up: 'fa fa-chevron-up',
                        down: 'fa fa-chevron-down',
                        previous: 'fa fa-chevron-left',
                        next: 'fa fa-chevron-right',
                        today: 'fa fa-dot-circle-o',
                        clear: 'fa fa-trash',
                        close: 'fa fa-times'
                    }
                });
                $ctrl.locales = Locales;

                $scope.$watch('$ctrl.locales.selected', code => {
                    let locale = _.find(Locales, {code: code});

                    if (!locale) return;

                    $ctrl.input.data('DateTimePicker').locale(locale.moment);
                });

                /**
                 * Datetimepicker emits changeDate event when user has picked a datetime
                 */
                $ctrl.input.on('dp.change', function(event) {
                    let date = moment(event.date).startOf('minute');

                    if (!$ctrl.ngModel.$modelValue) {
                        date.startOf('hour');

                        $ctrl.input.data('DateTimePicker').date(date);
                    }

                    output(date.format(f.input));
                });

                $scope.$watch('$ctrl.ngModel.$modelValue', input);

                // timezone changes
                $scope.$watch('$ctrl.timezone', function(t, o) {
                    if (!t) return;

                    let date = moment(($ctrl.ngModel.$modelValue || '').slice(0, 19), f.input);

                    output(date.format(f.input));
                });

                // utc offset of ngModel changes, update timezone
                $scope.$watch(function() {
                    if (_.get($ctrl.ngModel.$modelValue, 'length', 0) < 20 || !$ctrl.timezone) return null;

                    return $ctrl.ngModel.$modelValue.slice(19);
                }, function(gmtOffset, old) {
                    if (!gmtOffset) return;

                    let currentOffset = moment().tz($ctrl.timezone).format('Z');

                    if (currentOffset !== gmtOffset) $ctrl.timezone = gmtOffset;
                });

                if (!$ctrl.timezone && _.has($attrs, 'timezone')) {
                    // This will set the timezone to the current browser timezone
                    $ctrl.timezone = moment.tz.guess();
                }

                $attrs.$observe('disabled', function(disabled) {
                    let disable = disabled === false || _.isNil(disable);

                    $ctrl.input.data('DateTimePicker')[disable ? 'disable' : 'enable']();
                });
            };

            function clearInput(event) {
                if (event.target.value === '') $ctrl.ngModel.$setViewValue(null);
            }

            /**
             * Update ngModel with an ISO8601 datetime string with timezone
             *
             * @param {String} date ISO8601 datetime string
             */
            function output(date) {
                if (!$ctrl.timezone) {
                    // No timezone given, use localtime
                    date = moment(date, f.input);
                } else if (/[+-]\d+:\d+/.test($ctrl.timezone)) {
                    // Timezone is a UTC offset
                    date = moment(date, f.input).utcOffset($ctrl.timezone, true);
                } else {
                    // Timezone is a Zone Name (Europe/Amsterdam)
                    date = moment.tz(date, f.input, $ctrl.timezone);
                }

                if (!date.isValid()) return;

                $ctrl.ngModel.$setViewValue(date.format(f.output));
            }

            /**
             * Outside ngModel has changed, update input field to reflect
             *
             * Will empty the field if an invalid/incomplete date is given
             * @param {String} model ISO8601 datetime string
             * @return {void}
             */
            function input(model) {
                let dtp = $ctrl.input.data('DateTimePicker');

                if (!model || model.length < 19) {
                    dtp.date(null);

                    return;
                }

                // This ignores the timezone of the input date.
                let date = moment(model.slice(0, 19), f.input);

                if (!date.isValid()) {
                    dtp.date(null);

                    return;
                }

                dtp.date(date);

                // If the input model has a timestamp, we don't want to ignore it.
                if (model.length > 19) $ctrl.timezone = model.slice(19);
            }
        }
    }).name;
